import React, { useState, useEffect } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  FiHome, FiVideo, FiUsers, FiSearch, FiRadio,
  FiLogOut, FiMenu, FiX, FiUser, FiFileText, FiSettings, FiCheckCircle, FiFolder, FiAward
} from 'react-icons/fi';
import { getModeratorCompanyHost, setModeratorCompanyHost } from '../utils/companyScope';
import './Layout.css';

const Layout = () => {
  const { user, logout, isModerator, isModeratorOrManager } = useAuth();
  const location = useLocation();
  const isClientMode = user?.role === 'client';
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [scopeHost, setScopeHost] = useState(getModeratorCompanyHost());
  const [interfaceSettings, setInterfaceSettings] = useState({
    logo_url: null,
    website_url: '',
    primary_color: '#ff6b35',
    secondary_color: '#ff8c5a',
    accent_color: '#ffa07a',
    timestamp_theory_color: '#d1ecf1',
    timestamp_practice_color: '#fff3cd',
    timestamp_theory_badge_color: '#17a2b8',
    timestamp_practice_badge_color: '#ffc107',
  });
  const [serverMemory, setServerMemory] = useState(null);

  const fetchServerMemory = async () => {
    if (!isModeratorOrManager) return;
    try {
      const response = await axios.get('/api/health/server-memory/');
      setServerMemory(response.data || null);
    } catch (error) {
      setServerMemory(null);
    }
  };

  useEffect(() => {
    fetchInterfaceSettings();
    if (isModerator) {
      axios.get('/api/companies/').then((r) => {
        const list = Array.isArray(r.data) ? r.data : r.data?.results || [];
        setCompanies(list);
      }).catch(() => setCompanies([]));
    }
    const onScopeChange = () => {
      setScopeHost(getModeratorCompanyHost());
      fetchInterfaceSettings();
    };
    window.addEventListener('moderator-company-scope-changed', onScopeChange);
    return () => window.removeEventListener('moderator-company-scope-changed', onScopeChange);
  }, [isModerator]);

  useEffect(() => {
    if (!isModeratorOrManager) return undefined;
    fetchServerMemory();
    const timer = window.setInterval(fetchServerMemory, 60000);
    return () => window.clearInterval(timer);
  }, [isModeratorOrManager]);

  useEffect(() => {
    // Применяем цвета через CSS переменные
    if (interfaceSettings.primary_color) {
      document.documentElement.style.setProperty('--primary-color', interfaceSettings.primary_color);
    }
    if (interfaceSettings.secondary_color) {
      document.documentElement.style.setProperty('--secondary-color', interfaceSettings.secondary_color);
    }
    if (interfaceSettings.accent_color) {
      document.documentElement.style.setProperty('--accent-color', interfaceSettings.accent_color);
    }
    if (interfaceSettings.timestamp_theory_color) {
      document.documentElement.style.setProperty('--timestamp-theory-color', interfaceSettings.timestamp_theory_color);
    }
    if (interfaceSettings.timestamp_practice_color) {
      document.documentElement.style.setProperty('--timestamp-practice-color', interfaceSettings.timestamp_practice_color);
    }
    if (interfaceSettings.timestamp_theory_badge_color) {
      document.documentElement.style.setProperty('--timestamp-theory-badge-color', interfaceSettings.timestamp_theory_badge_color);
    }
    if (interfaceSettings.timestamp_practice_badge_color) {
      document.documentElement.style.setProperty('--timestamp-practice-badge-color', interfaceSettings.timestamp_practice_badge_color);
    }
  }, [interfaceSettings]);

  const fetchInterfaceSettings = async () => {
    try {
      const response = await axios.get('/api/settings/interface/');
      setInterfaceSettings(response.data);
    } catch (error) {
      // Fallback на старый endpoint если новый не работает
      try {
        const fallbackResponse = await axios.get('/api/health/interface/');
        setInterfaceSettings(fallbackResponse.data);
      } catch (fallbackError) {
        console.error('Ошибка загрузки настроек интерфейса:', fallbackError);
      }
    }
  };

  const menuItems = [
    { path: '/webinars', icon: FiVideo, label: 'Вебинары' },
    { path: '/live', icon: FiRadio, label: 'Прямой эфир' },
  ];

  if (isModeratorOrManager) {
    menuItems.push({ path: '/files', icon: FiFolder, label: 'Файлообменник' });
    menuItems.push({ path: '/diplomas', icon: FiAward, label: 'Дипломы' });
  }

  if (isModerator) {
    menuItems.unshift({ path: '/dashboard', icon: FiHome, label: 'Дашборд' });
    menuItems.push({ path: '/speakers', icon: FiUser, label: 'Спикеры' });
    menuItems.push({ path: '/search', icon: FiSearch, label: 'Поиск' });
    menuItems.push({ path: '/users', icon: FiUsers, label: 'Пользователи' });
    menuItems.push({ path: '/clients', icon: FiUsers, label: 'Клиенты' });
    menuItems.push({ path: '/logs/system', icon: FiFileText, label: 'Системные логи' });
    menuItems.push({ path: '/settings', icon: FiSettings, label: 'Настройки подключений' });
    menuItems.push({ path: '/interface-settings', icon: FiSettings, label: 'Настройки интерфейса' });
    menuItems.push({ path: '/health', icon: FiCheckCircle, label: 'Проверка подключений' });
  }

  const handleLogout = () => {
    logout();
  };

  const getMemoryStatusClass = () => {
    const ram = serverMemory?.ram;
    const disk = serverMemory?.disk;
    const ramLow = ram && ram.available_bytes / ram.total_bytes < 0.1;
    const diskLow = disk && disk.free_bytes / disk.total_bytes < 0.1;
    if (ramLow || diskLow) return 'server-memory-status warning';
    return 'server-memory-status';
  };

  const renderServerMemoryStatus = () => {
    if (!isModeratorOrManager || !serverMemory) return null;
    const { ram, disk } = serverMemory;
    return (
      <div className={getMemoryStatusClass()}>
        {ram ? (
          <div className="server-memory-line">
            RAM: <strong>{ram.available_display}</strong> свободно
          </div>
        ) : null}
        {disk ? (
          <div className="server-memory-line">
            Диск: <strong>{disk.free_display}</strong> свободно
          </div>
        ) : null}
      </div>
    );
  };

  if (isClientMode) {
    return (
      <div className="layout client-layout">
        <div className="main-content client-main-content">
          <main className="content client-content">
            <Outlet />
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="layout">
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-branding">
            {interfaceSettings.logo_url ? (
              interfaceSettings.website_url ? (
                <a 
                  href={interfaceSettings.website_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="sidebar-logo-link"
                >
                  <img src={interfaceSettings.logo_url} alt="Логотип" className="sidebar-logo" />
                </a>
              ) : (
                <img src={interfaceSettings.logo_url} alt="Логотип" className="sidebar-logo" />
              )
            ) : (
              <h2>Webinar AI</h2>
            )}
            {renderServerMemoryStatus()}
          </div>
          <button className="sidebar-toggle" onClick={() => setSidebarOpen(false)}>
            <FiX />
          </button>
        </div>
        <nav className="sidebar-nav">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-item ${isActive ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <Icon />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="sidebar-footer">
          <div className="user-info">
            <FiUser />
            <span>{user?.email}</span>
          </div>
          <button className="btn-logout" onClick={handleLogout}>
            <FiLogOut />
            <span>Выход</span>
          </button>
        </div>
      </aside>

      <div className="main-content">
        <header className="header">
          <button className="mobile-menu-toggle" onClick={() => setSidebarOpen(true)}>
            <FiMenu />
          </button>
          <h1 className="page-title">
            {menuItems.find(item => item.path === location.pathname)?.label || 'Платформа'}
          </h1>
          {isModerator && companies.length > 0 && (
            <div className="company-scope-bar">
              <label htmlFor="company-scope-select">Фирма:</label>
              <select
                id="company-scope-select"
                className="company-scope-select"
                value={scopeHost}
                onChange={(e) => {
                  const v = e.target.value;
                  setScopeHost(v);
                  setModeratorCompanyHost(v);
                }}
              >
                <option value="">Все фирмы</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.domain}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </header>
        <main className="content">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
